/** @format */

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useConnectModal, useAccountModal } from "@rainbow-me/rainbowkit";
import { useAccount, useDisconnect } from "wagmi";
import { toast } from "react-toastify";
import { formatUnits } from "viem";
import { useStaking } from "../hooks";
import { STAKING, hyperliquidEvmConfig } from "../constants";
import wlBgVideo from "../assets/stake.mp4";
// Import randomly selected hypurr images
import img0 from "../assets/hypurr/shrug.png";
import img1 from "../assets/hypurr/shook.png";
import img2 from "../assets/hypurr/teacher angry.png";
import img3 from "../assets/hypurr/crystalball.png";
import img4 from "../assets/hypurr/liquid.png";
import img5 from "../assets/hypurr/fire smirk.png";
import img6 from "../assets/hypurr/meowdy.png";
import img7 from "../assets/hypurr/teacher-bow.png";
import img8 from "../assets/hypurr/dead.png";
import img9 from "../assets/hypurr/thumbs up sad.png";
import img10 from "../assets/hypurr/dafuq.png";
import img11 from "../assets/hypurr/karate.png";
import img12 from "../assets/hypurr/samurai.png";
import img13 from "../assets/hypurr/cash.png";

const hypurrImages = [
  img0,
  img1,
  img2,
  img3,
  img4,
  img5,
  img6,
  img7,
  img8,
  img9,
  img10,
  img11,
  img12,
  img13,
];

// Define the pulsing glow animation
const glowAnimationStyle = `
@keyframes pulsing-glow {
  0% { filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5)); }
  50% { filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.8)); }
  100% { filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5)); }
}
.pulsing-cat {
  animation: pulsing-glow 3s infinite ease-in-out;
}
/* Added style for decorative images */
.pulsing-decoration {
  animation: pulsing-glow 3s infinite ease-in-out;
  width: 120px; /* Adjust size as needed */
  height: auto;
  position: absolute;
  pointer-events: none; /* Make sure they don't interfere with clicks */
}
`;

// Simple SVG Copy Icon Component
const CopyIcon = ({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-4 w-4 inline-block ${className}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

export function StakingPage() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoadingStakingData, setIsLoadingStakingData] = useState(true);
  const [displayCurrency, setDisplayCurrency] = useState<"PURR" | "USDC">(
    "PURR"
  );
  const [purrPrice, setPurrPrice] = useState<number | null>(null);
  const [isPriceLoading, setIsPriceLoading] = useState(true);
  const [priceError, setPriceError] = useState<string | null>(null);

  // Referral state
  const [referrerFromUrl, setReferrerFromUrl] = useState<string>("");
  const [manualReferrer, setManualReferrer] = useState<string>("");

  // Custom referrer code mappings
  const CUSTOM_REFERRER_CODES: Record<string, string> = {
    okto: "0x9B0b8f18Fd911101Da9a5F70757986C942f0699c",
    purrtardio: "0xf181CabA0A4318CeD4FFcC1e5F1521D714DB7574",
    // Add more custom codes here as needed
  };

  // RainbowKit and Wagmi hooks
  const { openConnectModal } = useConnectModal();
  const { openAccountModal } = useAccountModal();
  const { disconnect } = useDisconnect();
  const { address, isConnected } = useAccount();

  // Staking hook
  const {
    stakingData,
    stakingState,
    referralData,
    stakeAmount,
    unstakeAmount,
    setStakeAmount,
    setUnstakeAmount,
    approveTokens,
    stakeTokens,
    stakeTokensWithReferrer,
    unstakeTokens,
    setReferrer,
    formatTokenAmount,
    refetchAllData,
  } = useStaking();

  useEffect(() => {
    console.log("stakingData", stakingData);
  }, [stakingData]);

  // Validate Ethereum address format
  const isValidAddress = (address: string): boolean => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // Resolve referrer code to address
  const resolveReferrerCode = (input: string): string | null => {
    // First check if it's a custom code
    if (CUSTOM_REFERRER_CODES[input.toLowerCase()]) {
      return CUSTOM_REFERRER_CODES[input.toLowerCase()];
    }

    // If not a custom code and it's a valid address, return as is
    if (isValidAddress(input)) {
      return input;
    }

    // Invalid input
    return null;
  };

  // Read referrer from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get("referrer");

    if (refParam) {
      const resolvedAddress = resolveReferrerCode(refParam);

      if (resolvedAddress) {
        setReferrerFromUrl(resolvedAddress);
        setManualReferrer(resolvedAddress); // Pre-fill the manual input

        // Show different messages for custom codes vs addresses
        const isCustomCode = CUSTOM_REFERRER_CODES[refParam.toLowerCase()];
        const displayMessage = isCustomCode
          ? `Referrer code "${refParam}" detected (${resolvedAddress.slice(
              0,
              8
            )}...${resolvedAddress.slice(-6)})`
          : `Referrer detected: ${resolvedAddress.slice(
              0,
              8
            )}...${resolvedAddress.slice(-6)}`;

        console.log(
          "Valid referrer from URL:",
          resolvedAddress,
          isCustomCode ? `(code: ${refParam})` : ""
        );

        toast.info(displayMessage, {
          position: "bottom-right",
          autoClose: 5000,
          style: {
            background: "rgba(0, 0, 0, 0.9)",
            color: "#fff",
            fontFamily: "VT323",
            fontSize: "1.2rem",
            backdropFilter: "blur(5px)",
            border: "2px solid var(--neon-blue)",
            boxShadow: "0 0 10px var(--neon-pink)",
          },
        });
      } else {
        console.error("Invalid referrer code/address from URL:", refParam);
        toast.error(`Invalid referrer code or address: ${refParam}`, {
          position: "bottom-right",
          autoClose: 5000,
          style: {
            background: "rgba(50, 0, 0, 0.9)",
            color: "#ffaaaa",
            fontFamily: "VT323",
            fontSize: "1.2rem",
            backdropFilter: "blur(5px)",
            border: "2px solid #f00",
            boxShadow: "0 0 10px #f00",
          },
        });
      }
    }
  }, []);

  // Fetch PURR price
  const fetchPurrPrice = useCallback(async () => {
    setIsPriceLoading(true);
    setPriceError(null);
    try {
      const response = await fetch("https://api.hyperliquid.xyz/info", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "tokenDetails",
          tokenId: "0xc1fb593aeffbeb02f85e0308e9956a90", // PURR Token ID
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.midPx) {
        setPurrPrice(parseFloat(data.midPx));
      } else {
        throw new Error("Invalid price data received");
      }
    } catch (error) {
      console.error("Failed to fetch PURR price:", error);
      setPriceError("Failed to load PURR price.");
      // Fallback or default price could be set here if needed
    } finally {
      setIsPriceLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPurrPrice();
  }, [fetchPurrPrice]);

  // Simulate loading for a smoother UX (optional)
  useEffect(() => {
    // Simulate a short loading period to prevent UI flashing
    const stakingDataTimer = setTimeout(() => {
      setIsLoadingStakingData(false);
    }, 1500);

    return () => {
      clearTimeout(stakingDataTimer);
    };
  }, []);

  useEffect(() => {
    if (!stakingState.isLoading && !isPriceLoading) {
      setIsLoadingStakingData(false);
    }
  }, [stakingState.isLoading, isPriceLoading]);

  // Auto-refresh data periodically
  useEffect(() => {
    const interval = setInterval(() => {
      refetchAllData();
    }, 15000); // Refresh every 15 seconds

    return () => clearInterval(interval);
  }, [refetchAllData]);

  // Generate referral link for current user
  const generateReferralLink = useCallback((userAddress: string) => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?referrer=${userAddress}`;
  }, []);

  // Handle setting referrer (separate transaction)
  const handleSetReferrer = useCallback(
    async (referrerInput: string) => {
      const resolvedAddress = resolveReferrerCode(referrerInput);

      if (!resolvedAddress) {
        toast.error("Please enter a valid Ethereum address or referrer code", {
          position: "bottom-right",
          style: {
            background: "rgba(50, 0, 0, 0.9)",
            color: "#ffaaaa",
            fontFamily: "VT323",
            fontSize: "1.2rem",
            backdropFilter: "blur(5px)",
            border: "2px solid #f00",
            boxShadow: "0 0 10px #f00",
          },
        });
        return;
      }

      if (resolvedAddress.toLowerCase() === address?.toLowerCase()) {
        toast.error("You cannot refer yourself!", {
          position: "bottom-right",
          style: {
            background: "rgba(50, 0, 0, 0.9)",
            color: "#ffaaaa",
            fontFamily: "VT323",
            fontSize: "1.2rem",
            backdropFilter: "blur(5px)",
            border: "2px solid #f00",
            boxShadow: "0 0 10px #f00",
          },
        });
        return;
      }

      try {
        await setReferrer(resolvedAddress);
      } catch (error: any) {
        console.error("Error setting referrer:", error);
        let errorMessage = "Failed to set referrer";

        if (error.message?.includes("Referrer already set")) {
          errorMessage =
            "You have already set a referrer and cannot change it.";
        } else if (error.message?.includes("Cannot refer yourself")) {
          errorMessage = "You cannot use your own address as a referrer.";
        } else if (error.message?.includes("Invalid referrer")) {
          errorMessage = "Please enter a valid referrer address.";
        }

        toast.error(errorMessage, {
          position: "bottom-right",
          style: {
            background: "rgba(50, 0, 0, 0.9)",
            color: "#ffaaaa",
            fontFamily: "VT323",
            fontSize: "1.2rem",
            backdropFilter: "blur(5px)",
            border: "2px solid #f00",
            boxShadow: "0 0 10px #f00",
          },
        });
      }
    },
    [address, setReferrer, resolveReferrerCode]
  );

  // Enhanced stake function with referrer support
  const handleStakeWithReferrer = useCallback(async () => {
    const referrerInput = referrerFromUrl || manualReferrer;
    if (referrerInput && !referralData.hasSetReferrer) {
      const resolvedAddress = resolveReferrerCode(referrerInput);
      if (resolvedAddress) {
        // Call enhanced stake function with referrer
        await stakeTokensWithReferrer(resolvedAddress);
      } else {
        // Call regular stake function if referrer is invalid
        await stakeTokens();
      }
    } else {
      // Call regular stake function
      await stakeTokens();
    }
  }, [
    referrerFromUrl,
    manualReferrer,
    referralData.hasSetReferrer,
    stakeTokensWithReferrer,
    stakeTokens,
    resolveReferrerCode,
  ]);

  const handleConnect = async () => {
    if (isConnected) {
      openAccountModal?.();
      return;
    }
    setIsConnecting(true);
    try {
      openConnectModal?.();
    } catch (error) {
      console.error("Failed to connect:", error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    if (disconnect) {
      disconnect();
      toast.info("Wallet disconnected", {
        position: "bottom-right",
        style: {
          background: "rgba(0, 0, 0, 0.9)",
          color: "#fff",
          fontFamily: "VT323",
          fontSize: "1.2rem",
          backdropFilter: "blur(5px)",
          border: "2px solid var(--neon-blue)",
          boxShadow: "0 0 10px var(--neon-pink)",
        },
      });
    }
  };

  // Copy address handler
  const handleCopyAddress = useCallback((address: string, label: string) => {
    if (!address) return;
    navigator.clipboard
      .writeText(address)
      .then(() => {
        toast.info(`${label} address copied!`, {
          position: "bottom-right",
          autoClose: 2000, // Auto close after 2 seconds
          style: {
            // Reuse existing style or customize
            background: "rgba(0, 0, 0, 0.9)",
            color: "#fff",
            fontFamily: "VT323", // Keep consistent font
            fontSize: "1.2rem",
            backdropFilter: "blur(5px)",
            border: "2px solid var(--neon-blue)", // Blue border for copy success
            boxShadow: "0 0 10px var(--neon-pink)", // Pink glow
          },
        });
      })
      .catch((err) => {
        console.error("Failed to copy address: ", err);
        toast.error("Failed to copy address.", {
          position: "bottom-right",
          autoClose: 2000,
          style: {
            // Consistent error style
            background: "rgba(50, 0, 0, 0.9)", // Dark red background
            color: "#ffaaaa", // Light red text
            fontFamily: "VT323",
            fontSize: "1.2rem",
            backdropFilter: "blur(5px)",
            border: "2px solid #f00", // Red border for error
            boxShadow: "0 0 10px #f00", // Red glow
          },
        });
      });
  }, []); // Empty dependency array as toast and navigator are stable

  // Input validators
  const handleStakeAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimals
    if (/^\d*\.?\d*$/.test(value)) {
      setStakeAmount(value);
    }
  };

  const handleUnstakeAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    // Only allow numbers and decimals
    if (/^\d*\.?\d*$/.test(value)) {
      setUnstakeAmount(value);
    }
  };

  // Calculate time-based metrics
  const getTimeUntilNextReward = () => {
    // This is a placeholder - implement based on your contract's reward logic
    return "Rewards accrue in real-time";
  };

  // Format large numbers for display
  const formatNumberWithCommas = (x: string) => {
    // Split the number at the decimal point
    const parts = x.toString().split(".");
    // Add commas to the integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    // Join back with the decimal part if it exists
    return parts.join(".");
  };

  // Format amount for display based on selected currency
  const formatDisplayAmount = (amountWei: bigint | undefined): string => {
    if (amountWei === undefined)
      return displayCurrency === "USDC" ? "$0.00" : `0 ${STAKING.TOKEN_SYMBOL}`;

    const amountFormatted = formatUnits(amountWei, STAKING.TOKEN_DECIMALS); // Use formatUnits
    const amountNum = parseFloat(amountFormatted);

    if (displayCurrency === "USDC") {
      if (purrPrice !== null) {
        const usdValue = amountNum * purrPrice;
        return `$${formatNumberWithCommas(usdValue.toFixed(2))}`; // Format as currency
      } else {
        return "$ -.--"; // Indicate price not available
      }
    } else {
      // Display as PURR
      return `${formatNumberWithCommas(
        amountNum.toFixed(STAKING.TOKEN_DECIMALS > 0 ? 2 : 0)
      )} ${STAKING.TOKEN_SYMBOL}`; // Show 2 decimals for PURR if it has decimals
    }
  };

  const toggleDisplayCurrency = () => {
    setDisplayCurrency((current) => (current === "PURR" ? "USDC" : "PURR"));
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Video Background (replacing SVG) */}
      <div className="absolute inset-0 z-0">
        <video className="video-background" autoPlay loop muted playsInline>
          <source src={wlBgVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {/* Overlay to ensure text is readable */}
        <div className="video-overlay"></div>
      </div>

      {/* Decorative Images Layer */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {hypurrImages.map((imgSrc, index) => {
          // Generate random positions along the edges
          const side = Math.floor(Math.random() * 4); // 0: top, 1: right, 2: bottom, 3: left
          const position = Math.random() * 80 + 10; // % along the edge (10% to 90%)
          const style: React.CSSProperties = {};
          const randomDelay = Math.random() * 2; // Stagger animation start

          switch (side) {
            case 0: // Top edge
              style.top = `${Math.random() * 5}%`; // Randomize vertical distance slightly
              style.left = `${position}%`;
              break;
            case 1: // Right edge
              style.top = `${position}%`;
              style.right = `${Math.random() * 5}%`; // Randomize horizontal distance slightly
              break;
            case 2: // Bottom edge
              style.bottom = `${Math.random() * 5}%`; // Randomize vertical distance slightly
              style.left = `${position}%`;
              break;
            case 3: // Left edge
              style.top = `${position}%`;
              style.left = `${Math.random() * 5}%`; // Randomize horizontal distance slightly
              break;
          }

          style.animationDelay = `${randomDelay}s`; // Apply random delay

          return (
            <motion.img
              key={index}
              src={imgSrc}
              alt={`Hypurr decoration ${index}`}
              className="pulsing-decoration"
              style={style}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 0.7, scale: 1 }} // Fade in with slight transparency
              transition={{ duration: 1.5, delay: 0.5 + index * 0.1 }} // Staggered fade-in
            />
          );
        })}
      </div>

      {/* Add the style tag for the animation */}
      <style>{glowAnimationStyle}</style>

      {/* Content */}
      <div className="relative z-10 p-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl font-bold mb-4 glitch-text">STAKE PURR</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Stake your {STAKING.TOKEN_SYMBOL} tokens to earn reward points.
              Connect your wallet or use the form below.
            </p>
          </motion.div>

          {/* Connection Status */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {isConnected ? (
              <div className="flex items-center justify-center gap-2">
                <span className="text-green-400">●</span>
                <span className="mr-2">
                  Connected: {address?.slice(0, 6)}...{address?.slice(-4)}
                </span>
                <button
                  onClick={handleDisconnect}
                  className="px-3 py-1 bg-red-500/20 text-red-400 rounded border border-red-500/50 hover:bg-red-500/30 transition-all"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={handleConnect}
                disabled={isConnecting}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-2xl font-bold rounded-lg shadow-lg hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 border-2 border-white/20 backdrop-blur-sm"
              >
                {isConnecting ? "Connecting..." : "Connect Wallet"}
              </button>
            )}
          </motion.div>

          {/* Staking Dashboard */}
          {!isLoadingStakingData && !isPriceLoading && purrPrice !== null ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* Staking Stats */}
              <motion.div
                className="col-span-1 lg:col-span-2 p-6 border-2 border-purple-500 rounded-lg bg-black/50 backdrop-blur-sm shadow-[0_0_15px_rgba(147,51,234,0.3)]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h2 className="text-2xl font-bold mb-4 text-center">
                  STAKING DASHBOARD
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <motion.div
                    className="bg-purple-900/30 p-4 rounded-lg border border-purple-500/50"
                    whileHover={{
                      scale: 1.02,
                      borderColor: "rgba(168, 85, 247, 0.8)",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <h3 className="text-lg font-semibold text-purple-300">
                      Your Staked Balance
                    </h3>
                    <p className="text-2xl font-bold mt-2">
                      {formatDisplayAmount(stakingData.userStaked)}
                    </p>
                  </motion.div>
                  <motion.div
                    className="bg-purple-900/30 p-4 rounded-lg border border-purple-500/50"
                    whileHover={{
                      scale: 1.02,
                      borderColor: "rgba(168, 85, 247, 0.8)",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <h3 className="text-lg font-semibold text-purple-300">
                      Your Reward Points
                    </h3>
                    <p className="text-2xl font-bold mt-2">
                      {formatNumberWithCommas(
                        formatTokenAmount(stakingData.userPoints)
                      )}{" "}
                      PTS
                    </p>
                  </motion.div>
                  <motion.div
                    className="bg-purple-900/30 p-4 rounded-lg border border-purple-500/50"
                    whileHover={{
                      scale: 1.02,
                      borderColor: "rgba(168, 85, 247, 0.8)",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <h3 className="text-lg font-semibold text-purple-300">
                      Total Staked
                    </h3>
                    <p className="text-2xl font-bold mt-2">
                      {formatDisplayAmount(stakingData.totalStaked)}
                    </p>
                  </motion.div>
                </div>
                <motion.div
                  className="mt-4 p-3 bg-yellow-900/20 rounded-lg border border-yellow-500/30"
                  whileHover={{ borderColor: "rgba(234, 179, 8, 0.5)" }}
                  transition={{ duration: 0.2 }}
                >
                  <p className="text-yellow-300 text-center">
                    <span className="font-bold">Reward Rate:</span>{" "}
                    {formatTokenAmount(stakingData.pointRatePerTokenPerSecond)}{" "}
                    points per token per second
                  </p>
                  <p className="text-yellow-300 text-center mt-1">
                    <span className="font-bold">Next Update:</span>{" "}
                    {getTimeUntilNextReward()}
                  </p>
                </motion.div>
              </motion.div>

              {/* Referrer Input Section - Show if connected */}
              {isConnected && (
                <motion.div
                  className="col-span-1 lg:col-span-2 p-6 border-2 border-yellow-500 rounded-lg bg-black/50 backdrop-blur-sm shadow-[0_0_15px_rgba(234,179,8,0.3)]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                >
                  <h2 className="text-2xl font-bold mb-4 text-center">
                    REFERRAL SYSTEM
                  </h2>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Set Referrer Section */}
                    <div className="bg-yellow-900/30 p-4 rounded-lg border border-yellow-500/50">
                      <h3 className="text-lg font-semibold text-yellow-300 mb-3">
                        Set Your Referrer
                      </h3>
                      {!referralData.hasSetReferrer ? (
                        <div className="space-y-3">
                          <div>
                            <input
                              type="text"
                              value={manualReferrer}
                              onChange={(e) =>
                                setManualReferrer(e.target.value)
                              }
                              placeholder="Enter referrer address (0x...) or code (e.g., okto)"
                              className="w-full p-3 bg-gray-800/80 border border-gray-700 rounded-lg focus:outline-none focus:border-yellow-500 transition-all text-sm"
                            />
                            {referrerFromUrl && (
                              <p className="text-xs text-yellow-400 mt-1">
                                ✓ Referrer detected from URL:{" "}
                                {referrerFromUrl.slice(0, 8)}...
                                {referrerFromUrl.slice(-6)}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              You can enter either a wallet address (0x...) or a
                              referrer code (like "okto")
                            </p>
                          </div>
                          <motion.button
                            onClick={() => handleSetReferrer(manualReferrer)}
                            disabled={
                              !manualReferrer ||
                              !resolveReferrerCode(manualReferrer)
                            }
                            className="w-full p-3 bg-yellow-600 text-black font-bold rounded-lg hover:bg-yellow-500 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed transition-all"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            Set Referrer
                          </motion.button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className="text-green-400 font-bold">
                            ✅ Referrer Already Set
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            You can only set a referrer once per account
                          </p>
                          {referralData.userReferrer && (
                            <p className="text-xs text-yellow-400 mt-1">
                              Current referrer:{" "}
                              {referralData.userReferrer.slice(0, 8)}...
                              {referralData.userReferrer.slice(-6)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Share Referral Link Section */}
                    <div className="bg-yellow-900/30 p-4 rounded-lg border border-yellow-500/50">
                      <h3 className="text-lg font-semibold text-yellow-300 mb-3">
                        Your Referral Link
                      </h3>
                      <div className="space-y-3">
                        <div className="flex">
                          <input
                            type="text"
                            value={generateReferralLink(address || "")}
                            readOnly
                            className="flex-1 p-3 bg-gray-800/80 border border-gray-700 rounded-l-lg text-sm text-gray-300"
                          />
                          <button
                            onClick={() =>
                              handleCopyAddress(
                                generateReferralLink(address || ""),
                                "Referral link"
                              )
                            }
                            className="bg-yellow-600 px-4 rounded-r-lg border-t border-r border-b border-yellow-600 hover:bg-yellow-500 transition-all text-black font-bold"
                            title="Copy referral link"
                          >
                            <CopyIcon className="text-black" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 text-center">
                          Share this link to refer new users to the staking
                          platform
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Referral Stats */}
                  <div className="mt-4 p-3 bg-yellow-900/20 rounded-lg border border-yellow-500/30">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-yellow-400 font-bold text-lg">
                          {referralData.referralCount}
                        </p>
                        <p className="text-yellow-300 text-sm">
                          Users Referred
                        </p>
                      </div>
                      <div>
                        <p className="text-yellow-400 font-bold text-lg">
                          {referralData.referredUsersWithPoints.reduce(
                            (sum, user) => sum + parseInt(user.points || "0"),
                            0
                          )}
                        </p>
                        <p className="text-yellow-300 text-sm">
                          Total Referral Points
                        </p>
                      </div>
                      <div>
                        <p className="text-yellow-400 font-bold text-lg">
                          {referralData.referredUsers.length > 0
                            ? "Active"
                            : "None"}
                        </p>
                        <p className="text-yellow-300 text-sm">Status</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Stake Form - Conditionally render if connected */}
              {isConnected && (
                <motion.div
                  className="p-6 border-2 border-blue-500 rounded-lg bg-black/50 backdrop-blur-sm shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
                >
                  <h2 className="text-2xl font-bold mb-4 text-center">
                    STAKE TOKENS
                  </h2>
                  <div className="mb-4">
                    <p className="text-gray-300 mb-2">
                      Available: {formatDisplayAmount(stakingData.userBalance)}
                    </p>
                    <div className="flex">
                      <input
                        type="text"
                        value={stakeAmount}
                        onChange={handleStakeAmountChange}
                        placeholder={`Amount in ${STAKING.TOKEN_SYMBOL}`}
                        className="flex-1 p-3 bg-gray-800/80 border border-gray-700 rounded-l-lg focus:outline-none focus:border-blue-500 transition-all"
                      />
                      <button
                        onClick={() =>
                          setStakeAmount(
                            formatUnits(
                              stakingData.userBalance ?? 0n,
                              STAKING.TOKEN_DECIMALS
                            )
                          )
                        }
                        className="bg-gray-700 px-3 rounded-r-lg border-t border-r border-b border-gray-700 hover:bg-gray-600 transition-all"
                        title={`Set to max ${STAKING.TOKEN_SYMBOL}`}
                      >
                        MAX
                      </button>
                    </div>
                  </div>

                  {stakingState.needsApproval ? (
                    <motion.button
                      onClick={approveTokens}
                      disabled={
                        stakingState.isApprovalPending ||
                        stakingState.isApprovalMining ||
                        !isConnected
                      }
                      className="w-full p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {stakingState.isApprovalPending
                        ? "Check Wallet..."
                        : stakingState.isApprovalMining
                        ? "Approving..."
                        : "Approve Token Transfer"}
                    </motion.button>
                  ) : (
                    <motion.button
                      onClick={handleStakeWithReferrer}
                      disabled={
                        !stakeAmount ||
                        parseFloat(stakeAmount) <= 0 ||
                        stakingState.isStakingPending ||
                        !isConnected
                      }
                      className="w-full p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed transition-all"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {stakingState.isStakingPending
                        ? "Staking..."
                        : "Stake Tokens"}
                    </motion.button>
                  )}

                  {stakingState.error && (
                    <p className="mt-3 text-red-400 text-sm">
                      {stakingState.error}
                    </p>
                  )}
                </motion.div>
              )}

              {/* Unstake Form - Conditionally render if connected */}
              {isConnected && (
                <motion.div
                  className="p-6 border-2 border-pink-500 rounded-lg bg-black/50 backdrop-blur-sm shadow-[0_0_15px_rgba(236,72,153,0.3)]"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ boxShadow: "0 0 20px rgba(236,72,153,0.4)" }}
                >
                  <h2 className="text-2xl font-bold mb-4 text-center">
                    UNSTAKE TOKENS
                  </h2>
                  <div className="mb-4">
                    <p className="text-gray-300 mb-2">
                      Staked: {formatDisplayAmount(stakingData.userStaked)}
                    </p>
                    <div className="flex">
                      <input
                        type="text"
                        value={unstakeAmount}
                        onChange={handleUnstakeAmountChange}
                        placeholder={`Amount in ${STAKING.TOKEN_SYMBOL}`}
                        className="flex-1 p-3 bg-gray-800/80 border border-gray-700 rounded-l-lg focus:outline-none focus:border-pink-500 transition-all"
                      />
                      <button
                        onClick={() =>
                          setUnstakeAmount(
                            formatUnits(
                              stakingData.userStaked ?? 0n,
                              STAKING.TOKEN_DECIMALS
                            )
                          )
                        }
                        className="bg-gray-700 px-3 rounded-r-lg border-t border-r border-b border-gray-700 hover:bg-gray-600 transition-all"
                        title={`Set to max ${STAKING.TOKEN_SYMBOL}`}
                      >
                        MAX
                      </button>
                    </div>
                  </div>

                  <motion.button
                    onClick={unstakeTokens}
                    disabled={
                      !unstakeAmount ||
                      parseFloat(unstakeAmount) <= 0 ||
                      stakingState.isUnstakingPending ||
                      !isConnected
                    }
                    className="w-full p-3 bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-lg hover:from-pink-700 hover:to-pink-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {stakingState.isUnstakingPending
                      ? "Unstaking..."
                      : "Unstake Tokens"}
                  </motion.button>

                  {stakingState.error && (
                    <p className="mt-3 text-red-400 text-sm">
                      {stakingState.error}
                    </p>
                  )}
                </motion.div>
              )}
            </div>
          ) : (
            <motion.div
              className="my-10 p-6 border-2 border-purple-500 rounded-lg bg-black/50 backdrop-blur-sm text-center shadow-[0_0_15px_rgba(147,51,234,0.3)]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col items-center">
                {priceError ? (
                  <p className="text-xl mb-2 text-red-400">{priceError}</p>
                ) : (
                  <>
                    <p className="text-xl mb-2">Loading staking data...</p>
                    <div className="flex space-x-2 mt-2">
                      <motion.div
                        className="w-3 h-3 bg-purple-500 rounded-full"
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          repeatType: "loop",
                        }}
                      />
                      <motion.div
                        className="w-3 h-3 bg-purple-500 rounded-full"
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          repeatType: "loop",
                          delay: 0.2,
                        }}
                      />
                      <motion.div
                        className="w-3 h-3 bg-purple-500 rounded-full"
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          repeatType: "loop",
                          delay: 0.4,
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}

          {/* Additional Information Section */}
          <motion.div
            className="mt-8 p-6 border-2 border-gray-500 rounded-lg bg-black/50 backdrop-blur-sm shadow-[0_0_15px_rgba(107,114,128,0.3)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            whileHover={{ boxShadow: "0 0 20px rgba(107,114,128,0.4)" }}
          >
            <h2 className="text-2xl font-bold mb-4 text-center">
              ABOUT STAKING
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="text-xl font-semibold mb-3 text-pink-300 text-center">
                  How it Works
                </h3>
                <p className="text-gray-300 text-center">
                  Points have absolutely fucking zero monetary value.
                  <br />
                  <br />
                  {/* Points are an on-chain, arbitrary measure reflecting your contribution (duration and amount staked).                 */}
                </p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="text-xl font-semibold mb-3 text-blue-300 text-center">
                  Total Staked {STAKING.TOKEN_SYMBOL}
                </h3>
                <p className="text-3xl font-bold text-gray-100 mb-2 text-center">
                  {formatDisplayAmount(stakingData.totalStaked)}
                </p>
                <p className="text-gray-400 text-center">
                  This represents the total amount of {STAKING.TOKEN_SYMBOL}{" "}
                  currently staked by all users in the contract.
                </p>
              </motion.div>
            </div>

            {/* Contract Information */}
            <motion.div
              className="mt-6 p-4 border border-gray-600 rounded-lg bg-black/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <h3 className="text-xl font-semibold mb-3 text-gray-300 text-center">
                Contract Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-md font-medium text-purple-300 text-center">
                    Staking Contract:
                  </h4>
                  <div className="flex items-center justify-center text-gray-400 text-sm break-all">
                    <span>
                      {hyperliquidEvmConfig.contracts.stakingContract
                        ?.address || "Not configured"}
                    </span>
                    {hyperliquidEvmConfig.contracts.stakingContract
                      ?.address && (
                      <button
                        onClick={() =>
                          handleCopyAddress(
                            hyperliquidEvmConfig.contracts.stakingContract!
                              .address,
                            "Staking Contract"
                          )
                        }
                        className="ml-2 p-1 rounded hover:bg-purple-500/30 text-gray-500 hover:text-purple-300 transition-colors duration-200"
                        title="Copy Staking Contract Address"
                        aria-label="Copy Staking Contract Address"
                      >
                        <CopyIcon />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-md font-medium text-purple-300 text-center">
                    $PURR CA:
                  </h4>
                  <div className="flex items-center justify-center text-gray-400 text-sm break-all">
                    <span>
                      {hyperliquidEvmConfig.contracts.tokenContract?.address ||
                        "Not configured"}
                    </span>
                    {hyperliquidEvmConfig.contracts.tokenContract?.address && (
                      <button
                        onClick={() =>
                          handleCopyAddress(
                            hyperliquidEvmConfig.contracts.tokenContract!
                              .address,
                            "$PURR Contract"
                          )
                        }
                        className="ml-2 p-1 rounded hover:bg-pink-500/30 text-gray-500 hover:text-pink-300 transition-colors duration-200"
                        title="Copy $PURR Contract Address"
                        aria-label="Copy $PURR Contract Address"
                      >
                        <CopyIcon />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {(import.meta.env.VITE_ENVIRONMENT === "local" ||
                import.meta.env.VITE_ENVIRONMENT === "testnet") && (
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Using environment:{" "}
                  {import.meta.env.VITE_ENVIRONMENT || "mainnet"}
                </p>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Currency Toggle Button */}
      {!isPriceLoading && purrPrice !== null && (
        <button
          onClick={toggleDisplayCurrency}
          className="fixed bottom-5 right-5 z-20 px-4 py-2 bg-gradient-to-br from-gray-700 to-gray-900 text-white rounded-lg border border-gray-600 shadow-lg backdrop-blur-sm hover:from-gray-600 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black focus:ring-purple-500 transition-all text-sm"
          title={`Toggle display between ${STAKING.TOKEN_SYMBOL} and USDC`}
        >
          Show in{" "}
          {displayCurrency === "PURR" ? "USDC" : `$${STAKING.TOKEN_SYMBOL}`}
        </button>
      )}
      {isPriceLoading && (
        <div className="fixed bottom-5 right-5 z-20 px-4 py-2 bg-gray-800 text-gray-400 rounded-lg border border-gray-600 shadow-lg text-sm">
          Loading price...
        </div>
      )}
      {priceError && !purrPrice && (
        <div className="fixed bottom-5 right-5 z-20 px-4 py-2 bg-red-900/50 text-red-300 rounded-lg border border-red-700 shadow-lg text-sm">
          Price Error
        </div>
      )}
    </div>
  );
}
